import React, { useEffect, useRef, useState } from 'react';
import BEMHelper from '@ynap/bem-helper';
import { redirectToWishlist } from '@ynap/redirect-utils';
import Overlay, { StopPropagation } from '@ynap/overlay';
import { SizeSelect } from '@ynap/product-details-page';
import { useProductDetails } from '@ynap/api-hooks';
import Spinner from '@ynap/spinner';
import { Button } from '@ynap/button';
import { useConfig } from '@ynap/immutables-provider';
import { getItemFromWebStorage, setItemOnWebStorage } from '@ynap/web-storage-utils';
import { withOptimizely } from '@ynap/optimizely-utils';
import { notificationActions } from '@ynap/notification-state';
import { useDispatch } from 'react-redux';
import { IconAnimated } from '@ynap/animations';
import { constructUrl } from '@ynap/url-configuration';
import { safeBuildEventObj } from '@ynap/analytics';
import { trackAddToWishList } from '../analytics';
import { useTracking } from 'react-tracking';

const iframeThreshold = 10;

const bem = new BEMHelper('AddWishlistButton');
const ANIMATION_STATES = {
    stroke: 'stroke',
    filledMask: 'filledMask',
};

const ANIMATION_LOADING_TIME = 600;
const ANIMATION_FILLEDMASK_TIME = 2100;
const ANIMATION_END = 6000;

const AddWishlistButton = (props) => {
    const { product, locale, messages, wishlist, optimizely } = props;
    const [loginAndRegistrationModalOpen, setLoginAndRegistrationModalOpen] = useState(false);
    const [sizeModalOpen, setSizeModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [inWishList, setInWishList] = useState(false);
    const [animationState, setAnimationState] = useState('');
    const [iconDisabledState, setIconDisabledState] = useState(false);
    const [isUserLogged, setIsUserLogged] = useState();
    const [iframeHeight, setIframeHeight] = useState(0);
    const [selectedSku, setSelectedSku] = useState();
    const config = useConfig();
    const urlConfig = config?.global?.urlConfig;
    const hasAnimation = config?.head?.plp?.addWishListAnimation;
    const brand = config?.global?.application?.brandId;
    const signInIframeUrl = config?.head?.signInIframeUrl;
    const dispatch = useDispatch();
    const { trackEvent } = useTracking();

    const addToWishlist = async (sizeSku, closeModal) => {
        optimizely?.track?.('wl_sessionsadd');
        try {
            const response = await wishlist.addToWishList(product.partNumber, sizeSku);
            if (response.success) {
                closeModal?.();
                setInWishList(true);
                trackEvent(safeBuildEventObj(trackAddToWishList, { product, sizeSku }));
                return;
            }
            if (response.error) {
                const errorCode = response.error.errors?.[0]?.errorCode || 'UNKNOWN';
                setIconDisabledState(false);
                return dispatch(
                    notificationActions.setNotification({
                        name: 'action',
                        type: 'popup-error',
                        message: messages.notifications.errors({ error: errorCode }),
                    }),
                );
            }
        } catch (e) {
            if (`${e}`.includes('uberToken')) {
                setSelectedSku(sizeSku);
                setSizeModalOpen(false);
                setLoginAndRegistrationModalOpen(true);
            } else {
                setIconDisabledState(false);
                return dispatch(
                    notificationActions.setNotification({
                        name: 'action',
                        type: 'popup-error',
                        message: messages.notifications.errors({ error: 'UNKNOWN' }),
                    }),
                );
            }
        } finally {
            closeModal();
        }
    };

    useEffect(() => {
        const handleIframeInfo = (event) => {
            const { type, height, uberToken } = event.data || {};
            if (type === 'accountModalHeight' && iframeHeight !== height + iframeThreshold) {
                setIframeHeight(height + iframeThreshold);
            }
            if (type === 'updateUbertoken' && uberToken && !isUserLogged) {
                setIsUserLogged(uberToken);
            }
        };
        window.addEventListener('message', (event) => handleIframeInfo(event));
        return () => window.removeEventListener('message', (event) => handleIframeInfo(event));
    }, []);

    useEffect(() => {
        if (isUserLogged && selectedSku) {
            window.SF.notifyUserChange({ uberToken: isUserLogged });
            addToWishlist(selectedSku, () => setLoginAndRegistrationModalOpen(false));
        }
    }, [isUserLogged]);

    //Animation
    useEffect(() => {
        if (inWishList) {
            setAnimationState(ANIMATION_STATES['stroke']);

            const strokeTimeout = setTimeout(() => {
                setAnimationState(ANIMATION_STATES['filledMask']);
            }, ANIMATION_LOADING_TIME);

            const successMessageShown = getItemFromWebStorage('sessionStorage', 'AWLsuccessMessage');
            let openSuccessModalTimeout;
            if (!successMessageShown) {
                openSuccessModalTimeout = setTimeout(() => {
                    setSuccessModalOpen(true);
                }, ANIMATION_FILLEDMASK_TIME);
            }

            const resetAnimation = setTimeout(() => {
                if (successMessageShown) {
                    setAnimationState('');
                    setInWishList(false);
                    setIconDisabledState(false);
                }
            }, ANIMATION_END);

            return () => {
                clearInterval(strokeTimeout);
                clearInterval(openSuccessModalTimeout);
                clearInterval(resetAnimation);
            };
        }
    }, [inWishList]);

    // Reset animation after success message is closed
    useEffect(() => {
        if (!successModalOpen && inWishList) {
            const resetAnimation = setTimeout(() => {
                const successMessageShown = getItemFromWebStorage('sessionStorage', 'AWLsuccessMessage');
                if (successMessageShown) {
                    setAnimationState('');
                    setInWishList(false);
                    setIconDisabledState(false);
                }
            }, ANIMATION_END / 3);

            return () => {
                clearInterval(resetAnimation);
            };
        }
    }, [successModalOpen]);

    const handleClick = async () => {
        setSizeModalOpen(true);
        setIconDisabledState(true);

        optimizely?.track('plp_clickonwl');
    };

    const goToWishlist = () => {
        optimizely?.track('wl_sessionsview');
        redirectToWishlist(urlConfig, '', locale);
    };

    const renderIcon = () => {
        return (
            <div className={bem('contentIconAnimation')}>
                <IconAnimated
                    style={{ height: '20px', width: '20px' }}
                    icon={animationState}
                    loop={false}
                    brand={brand}
                    fallbackIcon={<div className={bem('contentIcon', { disabled: true })} />}
                />
            </div>
        );
    };

    return product?.notStockedOnline ? null : (
        <>
            {loginAndRegistrationModalOpen && (
                <LoginAndRegistrationModal
                    closeModal={() => {
                        setLoginAndRegistrationModalOpen(false);
                        setIconDisabledState(false);
                    }}
                    iframeHeight={iframeHeight}
                    signInIframeUrl={constructUrl(signInIframeUrl, { locale })}
                />
            )}
            {sizeModalOpen && !successModalOpen && (
                <SizeSelectModal
                    product={product}
                    closeModal={() => {
                        setSizeModalOpen(false);
                        setIconDisabledState(false);
                    }}
                    wishlist={wishlist}
                    messages={messages}
                    setInWishList={setInWishList}
                    setSuccessModalOpen={setSuccessModalOpen}
                    optimizely={optimizely}
                    addToWishlist={addToWishlist}
                    setSizeModalOpen={setSizeModalOpen}
                />
            )}
            {!sizeModalOpen && successModalOpen && (
                <SuccessModal
                    closeModal={() => {
                        setSuccessModalOpen(false);
                        setInWishList(true);
                    }}
                    messages={messages}
                    goToWishlist={goToWishlist}
                />
            )}
            <div className={bem('addWishlistButton')}>
                {hasAnimation && inWishList ? (
                    <div onClick={handleClick}>{renderIcon()}</div>
                ) : (
                    <div className={bem('contentIcon', { added: inWishList, disabled: iconDisabledState })} onClick={handleClick}></div>
                )}
            </div>
        </>
    );
};

export const SizeSelectModal = ({ product, setSizeModalOpen, closeModal, wishlist, messages, optimizely, addToWishlist }) => {
    const [productColor, setProductColor] = useState({});
    const sizeSelectRef = useRef(null);
    const productDetails = useProductDetails({
        partNumber: product.partNumber,
    });

    const { sKUs, price } = productColor;

    // Duplicate sku filtering https://git.yoox.net/projects/yos/repos/yos/browse/packages/state/product-details-state/src/selectors/getSelectedProduct.js?at=3f21483c4e44ef2bee81bf6b73f54115b574c1b9#350
    const skus = sKUs?.filter?.((sku) => sku.displayable && !sku.banned) || [];
    const hasMeaningfulSize = !['unsized', 'n/a', 'one size', 'onesize', 'yoox con'].includes(
        (String(productDetails?.details?.centralSizeScheme) || '').toLowerCase(),
    );

    useEffect(() => {
        if (!productDetails?.isProductDetailsLoading && productDetails?.details?.productColours) {
            setProductColor(productDetails.details.productColours[0]);
        }
    }, [productDetails]);

    const changeSelectedSku = async (sizeSku) => {
        optimizely?.track('plp_pdp_atw');
        addToWishlist(sizeSku, () => setSizeModalOpen(false));
    };

    const isLoading = Boolean(productDetails?.isProductDetailsLoading || wishlist?.isWishListLoading);

    useEffect(() => {
        if (skus?.length === 1 && !hasMeaningfulSize) {
            optimizely?.track('plp_pdp_atw');
            addToWishlist(skus[0], () => setSizeModalOpen(false));
        }
    }, [skus?.length]);

    if (skus?.length > 1 || (skus?.length === 1 && hasMeaningfulSize)) {
        return (
            <StopPropagation>
                <Overlay
                    className={{ container: bem('modal'), title: bem('overlayTitle') }}
                    borderStyle="borderStyleShort"
                    onClose={closeModal}
                    type="success"
                    title={messages.sizes.initialValue()}
                    handleBodyClick={closeModal}
                >
                    <Spinner loading={isLoading}>
                        <SizeSelect
                            sizeSelectRef={sizeSelectRef}
                            skus={skus}
                            displayLabel={false}
                            price={price}
                            messages={messages}
                            changeSelectedSku={(sizeSku) => {
                                changeSelectedSku(sizeSku);
                            }}
                            sizeGridFeatureToggle={true}
                            alwaysGrid={true}
                            disableOneSizeLeft={true}
                            className={bem('sizeSelect')}
                            headingClassName={bem('sizeSelectHeading')}
                            disableNotification={true}
                            hasMeaningfulSize={hasMeaningfulSize}
                        />
                    </Spinner>
                </Overlay>
            </StopPropagation>
        );
    }

    return null;
};

export const SuccessModal = ({ messages, closeModal, goToWishlist }) => {
    useEffect(() => {
        setItemOnWebStorage('sessionStorage', 'AWLsuccessMessage', true);
    }, []);
    return (
        <StopPropagation>
            <Overlay onClose={closeModal} handleBodyClick={closeModal} title="" type={'miniDialog'} className={{ container: bem('successModal'), title: bem('successOverlayTitle'), button: bem('successOverlayClose')}}>
                <div className={bem('success')} data-testid="atw_success">
                    <div className={bem('successTitle')}>{messages.productListing.wishList.title()}</div>
                    <div className={bem('successSubTitle')}>{messages.productListing.wishList.subtitle()}</div>
                    <Button type={'secondary'} onClick={goToWishlist} className={bem('successButton')}>
                        {messages.productListing.wishList.goToWishlist()}
                    </Button>
                </div>
            </Overlay>
        </StopPropagation>
    );
};

export const LoginAndRegistrationModal = ({ closeModal, iframeHeight, signInIframeUrl }) => {
    const pathToAccount = signInIframeUrl.startsWith('/') ? `${window.location.origin}${signInIframeUrl}` : signInIframeUrl;

    return (
        <StopPropagation>
            <Overlay onClose={closeModal} handleBodyClick={closeModal} title=" " className={{ container: bem('loginModal'), title: bem('signInOverlayTitle'), button: bem('signInOverlayClose')}}>
                <iframe className={bem('iframe')} src={pathToAccount} height={iframeHeight} />
            </Overlay>
        </StopPropagation>
    );
};

export default withOptimizely(AddWishlistButton);
